(ns app.domain.business.model
  (:require [app.domain.form.validators :as form-validators]
            [app.domain.group.model :as group-model]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [cloudpermit-commons.schemas.general :as general]
            [core.db.child-entity :as child]
            [core.util.schemas :as schemas]
            [core.util.string :as str-util]
            [schema-tools.core :as st]
            [schema.core :as s]))


(s/defschema BusinessEditInfo
  (st/merge {:business/type        s/Keyword
             :business/names       i18n-model/NonBlankLocalizedStringData
             :business/subdivision s/Keyword}
            (st/optional-keys {:business/trade-names     {s/Keyword s/Str}
                               :business/mailing-address general/NonBlankString
                               :business/municipality    general/NonBlankString
                               :business/postal-code     general/NonBlankString
                               :business/phone           general/NonBlankString
                               :business/email           general/NonBlankString
                               ;; E.g. a US FEIN or CA business number
                               :business/id              general/NonBlankString
                               :business/personal-id     general/NonBlankString
                               :business/mailing-country s/Keyword})))


(def new-business-attrs (keys (st/required-keys BusinessEditInfo)))


(defn check-edit-field
  "Validate a single business field `value` against `BusinessEditInfo`, mirroring the
   per-field editing surface used by the `:process.signal/edit-business` flow. Returns a
   schema error (truthy) or nil when valid.

   A nil/blank value means 'clear the field': it is valid only for optional keys; required
   keys (e.g. :business/type, :business/names, :business/subdivision) reject it. This matches
   the frontend `validator-fn`, which checks the per-field selection of the document against
   the per-field selection of the schema."
  [field value]
  (let [data (if (or (nil? value)
                     (and (string? value) (str/blank? value)))
               {}
               {field value})]
    (s/check (st/select-keys BusinessEditInfo [field]) data)))


(s/defschema AdditionalRequestData
  (st/merge {:party/party-roles (s/conditional (every-pred set? seq) #{s/Int}
                                               seq [s/Int]
                                               'at-least-one-role-is-required)}
            (st/optional-keys {:business/invite-email schemas/Email
                               :business/owner-type   (s/enum :business.owner-type/self :business.owner-type/other)
                               :party/permissions     (s/cond-pre #{s/Keyword} [s/Keyword])})))


(s/defschema NewBusinessRequest
  (st/merge BusinessEditInfo
            AdditionalRequestData))


(def new-business-req-attrs (keys (st/required-keys NewBusinessRequest)))


(s/defschema ExistingBusinessRequest
  (st/merge {:business/unique-id s/Str}
            AdditionalRequestData))


(def existing-business-req-attrs (keys (st/required-keys ExistingBusinessRequest)))


(defmethod i18n-model/get-localized-string-attrs :entity.type/business [_]
  [:business/names
   :business/trade-names])


(def ^{:arglists '([business lang-or-langs])} business-name
  (i18n-model/translation-fallback-fn :business/names))


(def ^{:arglists '([business lang-or-langs])} trade-name
  (i18n-model/translation-fallback-fn :business/trade-names))


(defn format-name [business lang-or-langs]
  (str (business-name business lang-or-langs)
       (when-let [trade-name (trade-name business lang-or-langs)]
         (str " dba " trade-name))))


(defn business-names-string-data
  [business]
  (->> business
       :business/names
       (map (fn [{:entity/keys [language]}]
              [language (format-name business language)]))
       (into {})))


(defn format-subdivision [business tr]
  (tr [(keyword "subdivision" (name (:business/subdivision business)))]))


(defn municipality-province [business tr]
  (let [province (format-subdivision business tr)]
    (if-some [municipality (str-util/blank->nil (:business/municipality business))]
      (str municipality ", " province)
      province)))


(defn get-user-key-no-ctx
  "[[get-user-key]] but only works when [[i18n-model/Ctx]] is not required, otherwise returns `nil`."
  [business k]
  (case k
    :user/street-address (:business/mailing-address business)
    :user/postal-code (:business/postal-code business)
    :user/municipality (:business/municipality business)
    :user/mobile-phone (:business/phone business)
    nil))


(s/defn get-user-key [{:keys [lang tr]} :- i18n-model/Ctx, business k]
  (or (get-user-key-no-ctx business k)
      (case k
        :user/province (format-subdivision business tr)
        :user/corporation-or-partnership (format-name business lang)
        nil)))


(defn full-address [business]
  (if-some [address (:business/mailing-address business)]
    (if-some [municipality (:business/municipality business)]
      (str address " " municipality)
      address)
    (get business :business/municipality "")))


(defn business-users [business]
  (some->> business
           :business/domain-group
           group-model/child-groups
           (mapcat :user/_groups)
           distinct))


(defn user-is-member? [business user]
  (->> (business-users business)
       (some #(= user %))))


(def business-attrs
  "Editable business attributes."
  [:business/names
   :business/type
   :business/id
   :business/personal-id
   :business/trade-names
   :business/mailing-address
   :business/municipality
   :business/mailing-country
   :business/subdivision
   :business/postal-code
   :business/phone
   :business/email])

(def business-attrs-edit-only
  "These attributes can be edited, but they are not shown in the party widget and if value is missing, also not on
   the business page."
  #{:business/email})

(defn personal-id-enabled? [{:business/keys [type]}]
  (= type :business.type/sole-proprietorship))


(defn business-parties-enabled?
  "Businesses can be applicants for licences and contractor registrations unconditionally (WIN-2803),
   and for all other permit types only when the business-parties-all-permit-types flag is enabled."
  [permit-type all-permit-types-enabled?]
  (or (contains? #{:permit-type/L :permit-type/CR} permit-type)
      all-permit-types-enabled?))


(defn business-country
  "Resolve country from the business's subdivision.
   Note that this will fail if the environment does not have
   subdivisions, but so will the existing business schema too."
  [{:business/keys [subdivision]}]
  (some-> subdivision name (str/split #"-") first keyword))


(defn personal-id-validator [business-data]
  (some-> business-data
          business-country
          form-validators/personal-id-validators))

(def business-id-validators
  {:CA (partial re-matches #"^\d{9}$")
   ;; valid prefixes per https://www.irs.gov/businesses/small-businesses-self-employed/how-eins-are-assigned-and-valid-ein-prefixes
   :US (partial re-matches #"^(01|02|03|04|05|06|10|11|12|13|14|15|16|20|21|22|23|24|25|26|27|30|31|32|33|34|35|36|37|38|39|40|41|42|43|44|45|46|46|47|48|50|51|52|53|54|55|56|57|58|59|60|61|62|63|64|65|66|67|68|71|72|73|74|75|76|77|80|81|82|83|84|85|85|86|86|87|87|88|88|90|91|92|92|93|93|94|95|98|99)-?\d{7}$")})

(defn valid-national-business-id? [{:business/keys [id]} country]
  (when id
    (some-> country
            keyword
            business-id-validators
            (apply [id])
            boolean)))

(def admin-group-name
  "Administrators")

(defn get-email-groups
  [business]
  (->> business
       :business/domain-group
       group-model/child-groups
       (filter #(-> % :group/roles (contains? :group.role/email-recipient)))
       seq))

(defn get-emails
  "Get emails to send mail to when a business party is the recipient.

  Currently, this deliberately does *not* include either :business/email nor
  the :notification/emails manual-input lists which may exist for a user group."
  [business]
  (->> business
       get-email-groups
       (mapcat :user/_groups)
       (map :user/email)
       (remove schemas/check-email)
       seq))

(def ^:private hidden-application-state?
  "application states hidden from business page"
  (du/in-state-fn :state.application/draft
                  :state.application/archived))

(defn visible-workspace?
  "Check if a given workspace should be visible in the business page"
  [workspace]
  (and
    ;; hide draft and archived
    (not (-> workspace workspace-model/workspace-application hidden-application-state?))
    ;; hide CR and L workspaces - they have their own widgets once issued
    (not (contains? #{:permit-type/L :permit-type/CR
                      ;; hide supplementals, they are accessible from the related B workspace
                      :permit-type/BS}
                    (:workspace/permit-type workspace)))))

(defn business-type [tr business]
  (tr [(:business/type business)]))

(def ^{:arglists '([business])} business-letters
  (child/child-entities-of-type-fn :entity.type/letter))
